import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useUploadQuote, useUploadQuoteBatch } from "@/api/queries";
import type { Process } from "@/api/types";
import { CompactUploadDropzone } from "@/components/upload/CompactUploadDropzone";
import { formatUploadError } from "@/lib/uploadErrors";
import { UploadErrorAdminInsight } from "@/components/UploadErrorAdminInsight";
import type { OperationTone } from "@/lib/operationTone";
import { maxUploadBytes } from "@/processes/registry";

/**
 * Per-section "Add another part" zone for the cart.
 *
 * Lives **inside** each ``OperationSection`` — uploads land directly
 * in that section's process. Replaces the legacy global multi-process
 * picker (removed): customers add a new operation by going back to
 * the upload page; once the operation has at least one item the
 * section appears with its own per-section dropzone.
 *
 * Visuals match ``Objednavka v2.html`` UploadZone (per-process title,
 * format chips, op tone on the icon badge).
 */
export function PerSectionAddMore({
  process,
  lang,
  tone,
}: {
  process: Process;
  lang: string;
  tone: OperationTone;
}): JSX.Element {
  const { t: tCart } = useTranslation("cart");
  const { t: tQuote } = useTranslation("quote");
  const navigate = useNavigate();

  const upload = useUploadQuote({
    onSuccess: (quote) => navigate(`/${lang}/quote/${quote.id}`),
  });
  const batch = useUploadQuoteBatch({
    onSuccess: (res) => navigate(`/${lang}/quote-batch/${res.public_id}`),
  });
  const pending = upload.isPending || batch.isPending;
  const errorMessage = formatUploadError(
    batch.error ?? upload.error,
    tQuote,
    process.max_upload_mb,
  );

  // The "add more parts" section is always scoped to a specific process, so
  // upload with ITS process_type (G6 widened the upload hooks to accept any
  // registered type). Previously clamped to laser/print_3d → a new process's
  // extra parts were silently analyzed as laser.
  const processType = String(process.name);

  const onFilesChosen = (files: File[]) => {
    if (files.length === 1) {
      upload.mutate({ file: files[0], lang, processType });
    } else {
      batch.mutate({ files, lang, processType });
    }
  };

  const acceptedLabel = process.accepted_file_types.join(" / ");

  return (
    <div data-testid={`cart-section-add-more-${process.name}`} aria-busy={pending}>
      <CompactUploadDropzone
        disabled={pending}
        onFilesChosen={onFilesChosen}
        errorMessage={errorMessage}
        statusLabel={
          pending
            ? tQuote("upload.analyzing")
            : null
        }
        acceptedFileTypes={process.accepted_file_types}
        maxBytes={maxUploadBytes(process)}
        title={tCart("perSectionAddMore.title", {
          defaultValue: "Přidat další díl pro {{name}}",
          name: process.display_name,
        })}
        hint={tCart("perSectionAddMore.hint", {
          defaultValue:
            "Přijímáme {{accepted}} — soubory se rovnou připojí do této operace.",
          accepted: acceptedLabel,
        })}
        iconStyle={{ background: tone.bg, color: tone.ink }}
      />
      <UploadErrorAdminInsight error={batch.error ?? upload.error} lang={lang} />
    </div>
  );
}
